// Google Analytics 4 — lightweight, env-driven, SPA-aware.
// Set `REACT_APP_GA4_MEASUREMENT_ID` in /app/frontend/.env (e.g. `G-XXXXXXXXXX`).
// When the env var is absent, this component renders nothing — zero overhead.
//
// USAGE: mount <GA4 /> INSIDE <BrowserRouter> so route-change page_views fire.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const GA_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
let injected = false;

export function isGaActive() {
  return Boolean(GA_ID);
}

/** Fire a custom GA4 event (no-op when GA isn't configured). */
export function trackEvent(name, params = {}) {
  if (!GA_ID || typeof window === "undefined" || !window.gtag) return;
  try {
    window.gtag("event", name, params);
  } catch {
    /* swallow analytics errors */
  }
}

export default function GA4() {
  const location = useLocation();

  // 1) Inject the gtag library once on mount.
  useEffect(() => {
    if (!GA_ID || injected || typeof window === "undefined") return;
    // HMR safety net: if a previous module-instance already injected the script,
    // don't append a duplicate during dev hot-reload.
    if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_ID}"]`)) {
      injected = true;
      return;
    }
    injected = true;

    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(s);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    // Disable auto page_view — the SPA tracker below handles every route change.
    window.gtag("config", GA_ID, {
      anonymize_ip: true,
      send_page_view: false,
    });
  }, []);

  // 2) Fire a page_view event on every React-Router location change (incl. first).
  useEffect(() => {
    if (!GA_ID || typeof window === "undefined" || !window.gtag) return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  return null;
}
